import { configureStore, EnhancedStore } from '@reduxjs/toolkit';
import createSagaMiddleware, { Task, SagaMiddleware } from 'redux-saga';
import { Saga } from 'redux-saga';
import { createReducer } from './reducers';
import { initialCommonState } from './slices/common/types';
import { setAdminToken, setCustomerToken } from '../api/client';
import type { InjectedReducersType, RootStateKeyType } from '../utils/types/injector-typings';
import type { RootState } from '../types/RootState';
import type { Reducer, AnyAction } from '@reduxjs/toolkit';

export type AppStore = EnhancedStore<RootState> & {
  injectedReducers: InjectedReducersType;
  injectedSagas: Record<string, Task>;
  runSaga: SagaMiddleware['run'];
  injectReducer: <Key extends RootStateKeyType>(
    key: Key,
    reducer: Reducer<Required<RootState>[Key], AnyAction>,
  ) => void;
  injectSaga: (key: string, saga: Saga) => void;
};

function hydrateAxiosTokens(): void {
  const adminToken = localStorage.getItem('sportshub_admin_token');
  const customerToken = localStorage.getItem('sportshub_customer_token');
  if (adminToken) setAdminToken(adminToken);
  if (customerToken) setCustomerToken(customerToken);
}

export function configureAppStore(): AppStore {
  hydrateAxiosTokens();

  const sagaMiddleware = createSagaMiddleware();

  const store = configureStore({
    reducer: createReducer(),
    middleware: (getDefault) =>
      getDefault({ thunk: false, serializableCheck: false }).concat(sagaMiddleware),
    preloadedState: {
      common: initialCommonState,
    } as Partial<RootState>,
  }) as AppStore;

  store.injectedReducers = {};
  store.injectedSagas = {};
  store.runSaga = sagaMiddleware.run.bind(sagaMiddleware);

  store.injectReducer = (key, reducer) => {
    if (Object.prototype.hasOwnProperty.call(store.injectedReducers, key)) {
      return;
    }
    store.injectedReducers[key] = reducer as InjectedReducersType[typeof key];
    store.replaceReducer(createReducer(store.injectedReducers));
  };

  store.injectSaga = (key, saga) => {
    if (Object.prototype.hasOwnProperty.call(store.injectedSagas, key)) {
      return;
    }
    // DAEMON: start once and keep running for the session
    store.injectedSagas[key] = store.runSaga(saga);
  };

  return store;
}

export const store = configureAppStore();

export type AppDispatch = typeof store.dispatch;
export type { RootState };
